import { useState } from 'react';

import { css, keyframes } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';

import {
	Assignee,
	EpicName,
	IssueTypeName,
	Priority,
} from '@/components/product-demos/animated-jira-tour/src/constants/ui.ts';
import { useAnimatedJiraBoardStore } from '@/components/product-demos/animated-jira-tour/src/state.ts';
import { wacToken } from '@/libraries/design/foundations';

import { BoardBody } from './board-body';
import { BoardHeader } from './board-header';
import { JiraIssue } from './constants/types';

const rootContainerStyle = css({
	maxHeight: '550px',
	maxWidth: '800px',
	width: '100%',
	backgroundColor: wacToken('color.neutral.light0'),
	borderRadius: '10px',
	padding: '8px',
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
	gap: '16px',
	boxShadow: `0px 18px 40px 0px #0000001A, 0px 73px 73px 0px #00000017, 0px 165px 99px 0px #0000000D, 0px 294px 118px 0px #00000003, 0px 459px 129px 0px #00000000`,
	[media.below.lg]: {
		maxWidth: '600px',
	},
});
const growBoard = keyframes({
	from: {
		maxHeight: '0px',
	},
	to: {
		maxHeight: '550px',
	},
});

const shrinkBoard = keyframes({
	from: {
		maxHeight: '550px',
	},
	to: {
		maxHeight: '0px',
	},
});

const fadeOut = keyframes({
	from: {
		opacity: 1,
	},
	to: {
		opacity: 0,
	},
});

const fadeIn = keyframes({
	from: {
		opacity: 0,
	},
	to: {
		opacity: 1,
	},
});

const animationBoardStyles = css({
	animation: `${fadeIn} 0.5s forwards, ${growBoard} 1s forwards, ${shrinkBoard} 1s forwards 11.75s, ${fadeOut} 0.5s forwards 11.75s`,
});

type JswAnimatedBoardProps = {
	inProgressCardsOverride?: Array<JiraIssue>;
	doneCardsOverride?: Array<JiraIssue>;
};

export const JswAnimatedBoard = (props: JswAnimatedBoardProps) => {
	const [{ messages }, { setInProgressCol, setDoneCol }] = useAnimatedJiraBoardStore();

	useState(() => {
		void setInProgressCol(
			props.inProgressCardsOverride ?? [
				{
					id: 'inProg1',
					summary: messages.inProgStaticLabel1,
					issueType: IssueTypeName.STORY,
					epicName: EpicName.FEATURES,
					assignee: Assignee.JANE,
					priority: Priority.BLOCKER,
				},
				{
					id: 'inProg2',
					summary: messages.inProgStaticLabel2,
					issueType: IssueTypeName.STORY,
					epicName: EpicName.ANALYTICS,
					assignee: Assignee.BLAKE,
					priority: Priority.HIGH,
				},
			],
		);
		void setDoneCol(
			props.doneCardsOverride ?? [
				{
					id: 'done1',
					summary: messages.doneStaticLabel1,
					issueType: IssueTypeName.BUG,
					epicName: EpicName.PAYMENTS,
					assignee: Assignee.ALVIN,
					priority: Priority.MINOR,
				},
				{
					id: 'done2',
					summary: messages.doneStaticLabel2,
					issueType: IssueTypeName.TASK,
					epicName: EpicName.FEATURES,
					assignee: Assignee.JANE,
					priority: Priority.CRITICAL,
				},
			],
		);
	});

	return (
		<div css={[rootContainerStyle, animationBoardStyles]}>
			<BoardHeader />
			<BoardBody />
		</div>
	);
};

export default JswAnimatedBoard;
